<template>
    <div class="columns is-mobile is-multiline layoutOptions">
        <div v-for="(layout, title) in layouts" :key="title" class="column is-half-mobile is-one-third-desktop">
            <div
                class="card is-clickable layoutOption"
                :class="{ 'is-active': layoutIsActive(layout) }"
                @click="selectLayout(title)"
            >
                <div class="card-header">
                    <div class="card-header-title">
                        <h4>{{ getLayoutTitle(title) }}</h4>
                    </div>
                </div>

                <div class="card-image" v-html="getLayoutSVG(layout)" />
            </div>
        </div>
    </div>
</template>

<script>
import groupBy from 'lodash/groupBy.js';
import sortBy from 'lodash/sortBy.js';
import startCase from 'lodash/startCase.js';

import Widget from '@/models/Widget.js';
import WidgetLayoutTemplate from '@/models/WidgetLayoutTemplate.js';

export default {
    name: 'LayoutOptions',

    emits: ['layout-selected'],

    data: () => ({
        layouts: null,
        loadingWidgets: false,
    }),

    computed: {
        /**
         * @returns {Collection<InstanceOf<Widget>>}
         */
        widgets() {
            return Widget.all();
        },
    },

    async created() {
        await WidgetLayoutTemplate.api().get('/api/widgets-template/layouts');

        this.layouts = groupBy(WidgetLayoutTemplate.all(), template => template.layout_name);
    },

    methods: {
        /**
         * @param layout
         * @returns {boolean}
         */
        layoutIsActive(layout) {
            return this.getLayoutSignature(this.widgets) === this.getLayoutSignature(layout);
        },

        /**
         * @param widgets
         * @returns {boolean}
         */
        getLayoutSignature(widgets) {
            return JSON.stringify(
                sortBy(
                    widgets.map(widget => {
                        /* eslint-disable id-denylist */
                        const { name, h, w, x, y } = widget.layout;

                        return { name, h, w, x, y };
                        /* eslint-enable id-denylist */
                    }),
                    widget => widget.name
                )
            );
        },

        /**
         * @param group
         * @returns {string}
         */
        getLayoutTitle(group) {
            return startCase(group);
        },

        /**
         * @param layout
         * @returns {string}
         */
        getLayoutSVG(layout) {
            const COLUMNS = 12;
            const ROWS = 20;
            const COLUMN_WIDTH = 10;
            const ROW_HEIGHT = 1;
            const GUTTER = 10;

            const viewBoxWidth = COLUMNS * COLUMN_WIDTH + GUTTER * (COLUMNS - 1);
            const viewBoxHeight = ROWS * ROW_HEIGHT + GUTTER * (ROWS - 1);

            let svg = `<svg viewBox="0 0 ${viewBoxWidth} ${viewBoxHeight}" fill="none" xmlns="http://www.w3.org/2000/svg">`;

            layout.forEach(widget => {
                svg += `<rect
                            x="${COLUMN_WIDTH * widget.layout_x + GUTTER * (widget.layout_x - 1) + GUTTER}"
                            y="${ROW_HEIGHT * widget.layout_y + GUTTER * (widget.layout_y - 1) + GUTTER}"
                            width="${COLUMN_WIDTH * widget.layout_width + GUTTER * (widget.layout_width - 1)}"
                            height="${ROW_HEIGHT * widget.layout_height + GUTTER * (widget.layout_height - 1)}"
                            rx="3"
                            stroke-width="1"
                        />`;
            });

            return (svg += '</svg>');
        },

        /**
         * @param layout
         * @returns {Promise<void>}
         */
        async selectLayout(layout) {
            if (this.loadingWidgets) return;

            Widget.query().deleteAll();

            this.loadingWidgets = true;

            try {
                await Widget.api().post('/api/widgets-template/layout', {
                    layout: this.getLayoutTitle(layout),
                });
            } finally {
                this.loadingWidgets = false;

                this.$emit('layout-selected');
            }
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.layoutOptions {
    .card-image {
        font-size: 0;
        padding: $card-content-padding;
        padding-top: 0;
    }
}

.layoutOption {
    svg {
        fill: $grey-lighter;
    }

    &:hover,
    &.is-active {
        h4,
        svg {
            color: $primary;
            fill: $primary-light;
        }
    }
}
</style>
